import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadSimple, FileText, Trash, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { useInvoiceViewer } from "@/components/InvoiceViewer";

const EMPTY = {
  cost_type: "", amount: 0, poc_name: "", poc_phone: "", poc_email: "",
  payment_status: "Unpaid", paid_amount: 0, gst_type: "Non-GST",
  invoice_file_id: null, invoice_filename: null,
  legal_name: "", gst_number: "", invoice_date: "", invoice_number: "",
  notes: "",
};

const COST_TYPES = ["Travel", "Courier", "Shoot fuel", "Equipment rental", "Location", "Food & stay", "Software", "Other"];

const fmtCur = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function AdditionalCostDialog({ open, onOpenChange, campaignId, editing, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [payForm, setPayForm] = useState({ amount: "", paid_at: "", gst_type: "Non-GST", notes: "" });
  const [showPayForm, setShowPayForm] = useState(false);
  const [logging, setLogging] = useState(false);
  const { openInvoice } = useInvoiceViewer();

  const editingId = editing?.id;

  const loadPayments = useCallback(async () => {
    if (!editingId) { setPayments([]); return; }
    try {
      const { data } = await api.get(`/additional-costs/${editingId}/payments`);
      setPayments(data);
    } catch (e) {
      // ignore
    }
  }, [editingId]);

  useEffect(() => {
    if (open) {
      const src = editing ? { ...EMPTY, ...editing } : EMPTY;
      setForm({ ...src, gst_type: src.gst_type || "Non-GST" });
      setShowPayForm(false);
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      setPayForm({ amount: "", paid_at: now.toISOString().slice(0, 16), gst_type: src.gst_type || "Non-GST", notes: "" });
      loadPayments();
    }
  }, [open, editing, loadPayments]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const totalCost = Number(form.amount || 0);
  const totalPaid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const pending = Math.max(totalCost - totalPaid, 0);

  const save = async () => {
    if (!form.cost_type.trim()) { toast.error("Cost type is required"); return; }
    const amount = parseFloat(form.amount || 0) || 0;
    if (amount < 0) { toast.error("Amount can't be negative"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount,
        paid_amount: editingId ? totalPaid : parseFloat(form.paid_amount || 0) || 0,
      };
      delete payload.gst_invoice_entries;
      const res = editingId
        ? await api.put(`/additional-costs/${editingId}`, payload)
        : await api.post(`/campaigns/${campaignId}/additional-costs`, payload);
      toast.success(editing ? "Cost updated" : "Cost added");
      onSaved?.(res.data);
      if (!editing) onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const logPayment = async () => {
    const amt = parseFloat(payForm.amount || 0);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!editingId) { toast.error("Save the cost first, then log payments"); return; }
    setLogging(true);
    try {
      const paidIso = payForm.paid_at ? new Date(payForm.paid_at).toISOString() : "";
      await api.post(`/additional-costs/${editingId}/payments`, {
        amount: amt, paid_at: paidIso, gst_type: payForm.gst_type, notes: payForm.notes,
      });
      toast.success("Payment logged");
      await loadPayments();
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      setPayForm({ amount: "", paid_at: now.toISOString().slice(0, 16), gst_type: form.gst_type || "Non-GST", notes: "" });
      setShowPayForm(false);
      onSaved?.({ ...editing, paid_amount: totalPaid + amt });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to log");
    } finally { setLogging(false); }
  };

  const removePayment = async (pid) => {
    if (!window.confirm("Delete this payment entry?")) return;
    try {
      await api.delete(`/cost-payments/${pid}`);
      await loadPayments();
      onSaved?.(editing);
      toast.success("Payment removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const uploadInvoice = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Detached upload — file is only linked to the cost when Save changes is clicked
      const { data } = await api.post(`/invoices/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("invoice_file_id", data.file_id);
      set("invoice_filename", data.filename);
      toast.success("Invoice staged — click Save changes to persist");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-cost-dialog" className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">{editing ? "Edit cost" : "Add cost"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Cost type *</Label>
              <Select value={form.cost_type || undefined} onValueChange={(v) => set("cost_type", v)}>
                <SelectTrigger className="mt-1" data-testid="cost-type-select"><SelectValue placeholder="Select or type" /></SelectTrigger>
                <SelectContent>
                  {COST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input value={form.cost_type} onChange={(e) => set("cost_type", e.target.value)} placeholder="Or type custom" className="mt-1.5 text-xs" data-testid="cost-type-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Amount (₹) *</Label>
              <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="mt-1" data-testid="cost-amount-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">POC name</Label>
              <Input value={form.poc_name} onChange={(e) => set("poc_name", e.target.value)} className="mt-1" data-testid="cost-poc-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">POC phone</Label>
              <Input value={form.poc_phone} onChange={(e) => set("poc_phone", e.target.value)} className="mt-1" data-testid="cost-poc-phone" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500">POC email</Label>
              <Input value={form.poc_email} onChange={(e) => set("poc_email", e.target.value)} className="mt-1" data-testid="cost-poc-email" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1" data-testid="cost-notes" />
            </div>
          </div>

          {/* Payments section */}
          <div className="border-t border-slate-200 pt-4 space-y-3" data-testid="cost-payments-section">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">Payments</div>
              <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" role="tablist" aria-label="GST toggle">
                {["GST", "Non-GST", "Reimbursement"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => { set("gst_type", v); setPayForm((f) => ({ ...f, gst_type: v })); }}
                    className={`text-xs px-3 py-1 ${form.gst_type === v ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`gst-toggle-${v.toLowerCase()}`}
                  >
                    {v === "Reimbursement" ? "Reimbursement" : `${v} payment`}
                  </button>
                ))}
              </div>
            </div>

            {form.gst_type === "GST" && (
              <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50 space-y-2" data-testid="cost-gst-invoice-details">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">GST invoice details</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Legal name</Label>
                    <Input value={form.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} className="mt-1" data-testid="cost-gst-legal-name" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">GST number</Label>
                    <Input value={form.gst_number || ""} onChange={(e) => set("gst_number", e.target.value)} className="mt-1" data-testid="cost-gst-number" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Invoice date</Label>
                    <Input type="date" value={form.invoice_date || ""} onChange={(e) => set("invoice_date", e.target.value)} className="mt-1" data-testid="cost-gst-invoice-date" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500">Invoice number</Label>
                    <Input value={form.invoice_number || ""} onChange={(e) => set("invoice_number", e.target.value)} className="mt-1" data-testid="cost-gst-invoice-number" />
                  </div>
                </div>
              </div>
            )}

            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-3">
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Cost</div>
                <div className="stat-num text-lg font-semibold text-slate-900 mt-0.5" data-testid="cost-summary-total">{fmtCur(totalCost)}</div>
              </div>
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Paid</div>
                <div className="stat-num text-lg font-semibold text-emerald-700 mt-0.5" data-testid="cost-summary-paid">{fmtCur(totalPaid)}</div>
              </div>
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Pending</div>
                <div className="stat-num text-lg font-semibold text-rose-600 mt-0.5" data-testid="cost-summary-pending">{fmtCur(pending)}</div>
              </div>
            </div>

            {!editingId ? (
              <div className="text-[11px] text-amber-600">Save the cost first to log payments and upload an invoice.</div>
            ) : (
              <>
                {!showPayForm ? (
                  <Button
                    variant="outline"
                    onClick={() => setShowPayForm(true)}
                    className="border-slate-300 h-9"
                    data-testid="cost-log-payment-btn"
                  >
                    <Plus size={14} className="mr-1.5" /> Log a new payment
                  </Button>
                ) : (
                  <div className="border border-slate-200 rounded-md p-3 bg-slate-50/60 space-y-2" data-testid="cost-log-payment-form">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-500">Amount (₹) *</Label>
                        <Input type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className="mt-1" data-testid="cost-log-amount" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-500">Paid on</Label>
                        <Input type="datetime-local" value={payForm.paid_at} onChange={(e) => setPayForm({ ...payForm, paid_at: e.target.value })} className="mt-1" data-testid="cost-log-date" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-500">Payment type</Label>
                        <Select value={payForm.gst_type} onValueChange={(v) => setPayForm({ ...payForm, gst_type: v })}>
                          <SelectTrigger className="mt-1" data-testid="cost-log-gst"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GST">GST</SelectItem>
                            <SelectItem value="Non-GST">Non-GST</SelectItem>
                            <SelectItem value="Reimbursement">Reimbursement</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-500">Notes</Label>
                        <Input value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} className="mt-1" data-testid="cost-log-notes" />
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-2">
                      <Button variant="outline" onClick={() => setShowPayForm(false)}>Cancel</Button>
                      <Button onClick={logPayment} disabled={logging} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="cost-log-save-btn">
                        {logging ? "Saving…" : "Log payment"}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Payment log */}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Payment log ({payments.length})</div>
                  {payments.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">No payments logged yet.</div>
                  ) : (
                    <div className="border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="cost-payment-log">
                      {payments.map((p) => (
                        <div key={p.id} className="flex items-center justify-between px-3 py-2 text-xs" data-testid={`cost-payment-${p.id}`}>
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-slate-600 min-w-[140px]">{fmtDate(p.paid_at)}</span>
                            <span className="font-mono font-semibold text-slate-900">{fmtCur(p.amount)}</span>
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${p.gst_type === "GST" ? "bg-blue-50 text-blue-700 border-blue-200" : (p.gst_type === "Reimbursement" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200")}`}>{p.gst_type || "Non-GST"}</span>
                            {p.notes && <span className="text-slate-500 truncate">· {p.notes}</span>}
                          </div>
                          <button
                            onClick={() => removePayment(p.id)}
                            className="text-slate-400 hover:text-rose-600 shrink-0"
                            title="Remove"
                            data-testid={`cost-payment-remove-${p.id}`}
                          >
                            <Trash size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="p-3 border border-dashed border-slate-300 rounded-md">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Invoice</div>
            {form.invoice_file_id ? (
              <div className="flex items-center justify-between bg-white border border-slate-200 rounded-md px-3 py-2">
                <button type="button" onClick={() => openInvoice(form.invoice_file_id, form.invoice_filename)}
                   className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700" data-testid="cost-invoice-link">
                  <FileText size={16} weight="duotone" /> {form.invoice_filename || "Invoice"}
                </button>
                <button onClick={() => { set("invoice_file_id", null); set("invoice_filename", null); }}
                        className="text-slate-400 hover:text-rose-600" data-testid="cost-invoice-remove">
                  <Trash size={16} />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center py-4 cursor-pointer text-slate-500 hover:text-blue-600">
                <UploadSimple size={22} />
                <div className="text-sm mt-1">{uploading ? "Uploading…" : "Upload invoice (PDF / image)"}</div>
                <input type="file" accept="application/pdf,image/*"
                       onChange={(e) => e.target.files?.[0] && uploadInvoice(e.target.files[0])}
                       className="hidden" data-testid="cost-invoice-upload" />
              </label>
            )}
            {!editingId && <div className="text-[11px] text-amber-600 mt-2">Save the cost first to enable invoice upload.</div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="cost-save-btn">
            {saving ? "Saving…" : editing ? "Save changes" : "Add cost"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
